import {
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Tooltip, Whisper } from "rsuite";
import { Col, Row } from "reactstrap";
import { useMemo } from "react";

import {
  getTextColor,
  isValidProgressChart,
  isValidProgressQuery,
} from "../../utils/DashboardUtils";
import useAggregationData from "./WidgetHooks/useAggregationData";
import { BaseChartProps } from "./WidgetTypes";
import useThemeColors from "./useThemeColors";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

const ProgressBarChartWidget = ({
  section,
  setSettings,
  theme,
  topLevelFilters,
  record,
  viewFilters,
}: BaseChartProps) => {
  const { chartData, chartLoading }: any = useAggregationData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    target: section?.Target,
    validator: isValidProgressQuery,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    record: record,
  });
  const { colors } = useThemeColors({ theme, chartData });

  const roundToTenth = (val: number) => {
    if (!val) return 0;

    return Math.round(val * 10) / 10;
  };

  const roundReadableValue = (val: number, fieldType = "") => {
    let value: string | number = val;

    if (value >= 1000 && value < 10000) {
      value = value / 1000;
      value = roundToTenth(value);
      value = `${value}K`;
    } else if (value >= 10000 && value < 100000) {
      value = value / 10000;
      value = roundToTenth(value);
      value = `${value}K`;
    } else if (value >= 100000 && value < 100000) {
      value = value / 100000;
      value = roundToTenth(value);
      value = `${value}K`;
    } else if (value >= 1000000 && value < 1000000000) {
      value = value / 1000000;
      value = roundToTenth(value);
      value = `${value}M`;
    } else if (value >= 1000000000) {
      value = value / 1000000000;
      value = roundToTenth(value);
      value = `${value}B`;
    }

    if (fieldType === "Currency" || fieldType === "CurrencyInterval") {
      return `$${value}`;
    }

    return value;
  };

  const maxChartPercent = () => {
    if (!chartData) {
      return;
    }

    const progress = chartData.percent?.split("%")[0];
    return progress >= 100;
  };

  const RingProgress = useMemo(
    () => (
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="10%"
          outerRadius="80%"
          barSize={10}
          data={chartData}
        >
          <RadialBar
            // minAngle={15}
            label={{ position: "insideStart", fill: "#fff" }}
            background
            // clockWise
            dataKey="uv"
          />
          <Legend
            iconSize={10}
            layout="vertical"
            verticalAlign="middle"
            // wrapperStyle={style}
          />
        </RadialBarChart>
      </ResponsiveContainer>
    ),
    [
      chartData?.percent,
      chartData?.readable,
      chartData?.targetReadable,
      chartData?.result,
      section?.Color,
    ]
  );

  const BarProgress = useMemo(
    () => (
      <div style={{ height: "100%", width: "100%" }}>
        <div style={{ height: "60%", maxHeight: 250 }} className="p-3">
          <Whisper
            placement="autoVertical"
            trigger="hover"
            speaker={
              <Tooltip>
                <p className="m-0">
                  <span className="font-weight-bold">
                    {chartData?.readable}
                  </span>{" "}
                  of {chartData?.targetReadable}
                </p>
              </Tooltip>
            }
          >
            <div
              style={{
                position: "relative",
                top: "50%",
                transform: "translateY(-50%)",
              }}
              className="cursor-pointer"
            >
              <Row className="align-items-center justify-content-center">
                <Col xs="auto">
                  <h1
                    className="m-0 display-3"
                    style={{ color: section?.Color || colors?.[0] }}
                  >
                    {roundReadableValue(chartData?.value, chartData?.fieldType)}
                  </h1>
                </Col>
                <Col xs="auto" className="border-left">
                  <h1 className="m-0 font-weight-500 text-dark">
                    {roundReadableValue(
                      chartData?.target,
                      chartData?.fieldType
                    )}
                  </h1>
                </Col>
              </Row>
            </div>
          </Whisper>
        </div>
        <div
          className="progress-chart bg-superlight"
          style={{
            height: "30%",
            maxHeight: 150,
            borderRadius: 4,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              height: "100%",
              left: 0,
              borderTopLeftRadius: 4,
              borderBottomLeftRadius: 4,
              background: section?.Color || colors?.[0],
              width: maxChartPercent() ? "100%" : chartData?.percent,
            }}
          ></div>
          <div
            style={{
              position: "absolute",
              color:
                chartData?.result?.[0]?.percent > 0.4
                  ? getTextColor(section?.Color || colors?.[0])
                  : "#000",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            {roundToTenth(chartData?.result?.[0]?.percent * 100) + "%"}
          </div>
        </div>
      </div>
    ),
    [
      chartData?.percent,
      chartData?.readable,
      chartData?.targetReadable,
      chartData?.result,
      section?.Color,
      chartData?.field,
    ]
  );

  if (!isValidProgressChart(section)) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  if (chartLoading) {
    return <WidgetLoading />;
  }

  // TODO: Readd support for Circular Progress Bar
  return section?.Orientation === "Bar" ? BarProgress : BarProgress;
};

export default ProgressBarChartWidget;
